import { useEffect,useRef,useState } from "react";
import { useReadingApi } from "./api";
import { readingDiagnostic } from "./telemetry";
import type { Question,QuestionsState } from "./types";
const QUESTIONS_POLL_MS = 4_000;
const QUESTIONS_DEADLINE_MS = 150_000;
// Questions are written on the server after the text arrives. Poll until they are
// ready or the server reports a failure, and stop waiting after a generous deadline.
export function useQuestions(storyId: string | undefined): QuestionsState {
  const { fetch, actor } = useReadingApi();
  const [retryVersion, setRetryVersion] = useState(0);
  const retryFor = useRef<string>();
  const [state, setState] = useState<QuestionsState>({ status: "idle", items: [] });

  useEffect(() => {
    if (!storyId) {
      setState({ status: "idle", items: [] });
      return;
    }
    const controller = new AbortController();
    const deadline = Date.now() + QUESTIONS_DEADLINE_MS;
    let cancelled = false;
    let timer = 0;
    setState({ status: "pending", items: [] });

    const poll = async () => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        readingDiagnostic("reading_questions_client_failed", { stage: "client_questions", reason: "poll_deadline" });
        setState({ status: "failed", items: [] });
        return;
      }
      try {
        const response = await fetch(`/api/questions?id=${encodeURIComponent(storyId)}`, { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(Math.min(10_000, remaining))]) });
        const data = (response.ok ? await response.json() : {}) as { status?: string; questions?: Question[] };
        if (cancelled) return;
        if (response.ok && data.status === "ready" && data.questions?.length) {
          setState({ status: "ready", items: data.questions });
          return;
        }
        if ((response.ok && data.status === "failed") || (!response.ok && response.status < 500 && ![408, 429].includes(response.status))) {
          readingDiagnostic("reading_questions_client_failed", { stage: "client_questions", reason: `http_${response.status}`, status: response.status });
          setState({ status: "failed", items: [] });
          return;
        }
      } catch {
        if (cancelled) return;
      }
      if (Date.now() > deadline) {
        setState({ status: "failed", items: [] });
        return;
      }
      timer = window.setTimeout(poll, QUESTIONS_POLL_MS);
    };

    const start = async () => {
      if (retryVersion && retryFor.current === storyId) {
        retryFor.current = undefined;
        try {
          const response = await fetch("/api/retry-questions", { method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: storyId }), signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]) });
          if (!response.ok) throw new Error("retry_failed");
        } catch {
          if (!cancelled) setState({ status: "failed", items: [] });
          return;
        }
      }
      if (!cancelled) timer = window.setTimeout(poll, QUESTIONS_POLL_MS);
    };
    void start();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [storyId, retryVersion, fetch]);

  return { ...state, ...(storyId && actor.actor === "child" ? { retry: () => { retryFor.current = storyId; setRetryVersion(value => value + 1); } } : {}) };
}

